import { useMemo } from 'react'
import { LineChartWrapper } from '../../charts/LineChart'
import { WidgetFrame } from '../../widgets/WidgetFrame'
import { useWidgetData } from '../../widgets/useWidgetData'
import { WIDGET_KEY, type WidgetProps } from '../../widgets/types'
import { buildDrillRsql } from '../../drill'
import { formatMoney, formatPercent } from '../../format'
import { runExport, type ExportColumn } from '../../../../lib/export'
import { buildYoYTrend, type YoYTrendPoint } from './analyticsMappers'
import type { CertifiedValueByFiscalYearResult } from '../../../../types/insights'

const WIDGET_KEY_SELF = WIDGET_KEY.certifiedValueByFiscalYear

/** Same disclosed constant as every other certified-value tile — every certified-value fact at launch is USD. */
const REPORTING_CURRENCY = 'USD'

const EXPORT_COLUMNS: ExportColumn<YoYTrendPoint>[] = [
  { id: 'fiscalYear', header: 'Fiscal year', toText: (p) => p.fiscalYearId },
  { id: 'sum', header: `Certified value (${REPORTING_CURRENCY})`, toText: (p) => formatMoney(String(p.sum), REPORTING_CURRENCY) },
]

/**
 * AC1.3 — YoY certified-value trend. New backend widget key (`certified-value-by-fiscal-year`,
 * `CertifiedValueRollup`'s `FISCAL_YEAR` axis) fed via the SAME `credit:R AND case:R` gate as its
 * sibling certified-value tiles. The %-delta badge renders "—" (never a fabricated "0%") whenever
 * `buildYoYTrend` can't honestly compute one — exactly 1 fiscal year of data, or a $0 prior year.
 */
export function CertifiedValueTrendWidget({ filter, skipRefetch, onDrill }: WidgetProps) {
  const { status, data, message, retry } =
    useWidgetData<CertifiedValueByFiscalYearResult>(WIDGET_KEY_SELF, filter, skipRefetch)

  const buckets = useMemo(() => (Array.isArray(data?.buckets) ? data.buckets : []), [data])
  const { points, delta } = useMemo(() => buildYoYTrend(buckets), [buckets])
  const excludedCount = typeof data?.excludedCount === 'number' && data.excludedCount > 0 ? data.excludedCount : 0
  // S3 hardening fix (Verifier finding on PR 203) — a fact whose filing had no matching dimension
  // row is counted here, never silently dropped; disclosed exactly like excludedCount so the "Total
  // certified value" story stays provably complete, never silently understated.
  const unresolvedCount = typeof data?.unresolvedCount === 'number' && data.unresolvedCount > 0 ? data.unresolvedCount : 0

  const frameState = status === 'ok' && points.length === 0 ? 'empty' : status

  return (
    <WidgetFrame
      title="Certified value trend (YoY)"
      icon="activity"
      state={frameState}
      message={message ?? 'No certified value yet for the current filter.'}
      onRetry={retry}
      drill={{ label: 'View filings', onClick: () => onDrill(buildDrillRsql(filter)) }}
      exportAction={points.length > 0 ? { onClick: () => runExport('csv', 'Certified value trend (YoY)', points, EXPORT_COLUMNS) } : undefined}
    >
      <LineChartWrapper<YoYTrendPoint>
        title="Certified value trend (YoY)"
        data={points}
        categoryKey="fiscalYearId"
        categoryLabel="Fiscal year"
        series={[{ key: 'sum', label: 'Certified value' }]}
        valueFormatter={(v) => formatMoney(String(v), REPORTING_CURRENCY)}
      />
      <p style={{ margin: 'var(--iw-space-2) 0 0', fontSize: 'var(--iw-size-sm)' }}>
        {delta ? (
          <span
            className={`iw-kpi-delta iw-kpi-delta--${delta.variant}`}
            aria-label={`${formatPercent(delta.pct)} versus the prior fiscal year`}
          >
            {formatPercent(delta.pct)} vs. prior year
          </span>
        ) : (
          <span style={{ color: 'var(--iw-text-muted)' }}>
            — (not enough fiscal-year history yet for a YoY comparison)
          </span>
        )}
      </p>
      {excludedCount > 0 ? (
        <p style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-sm)', margin: 'var(--iw-space-2) 0 0' }}>
          {excludedCount} certified value{excludedCount === 1 ? '' : 's'} excluded — non-{REPORTING_CURRENCY}{' '}
          currency, no FX rate applied.
        </p>
      ) : null}
      {unresolvedCount > 0 ? (
        <p style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-sm)', margin: 'var(--iw-space-2) 0 0' }}>
          {unresolvedCount} certified value{unresolvedCount === 1 ? '' : 's'} could not be attributed to a fiscal
          year and are not reflected above.
        </p>
      ) : null}
    </WidgetFrame>
  )
}
